import type { ExcludedIncome } from '@sea-snag/shared';

/**
 * Money in its own currency (#89). Income used to be printed with a literal
 * "€", whatever the bookings were in. Whole units: charter prices are.
 */
export function formatMoney(amount: number, currency: string | null | undefined): string {
  return formatter(currency).format(amount);
}

/** "€12k": the compact form the dashboard widgets use. */
export function formatThousands(amount: number, currency: string | null | undefined): string {
  const symbol =
    formatter(currency)
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? '';
  return `${symbol}${Math.round(amount / 1000)}k`;
}

/** Each boat's bookings in other currencies, added up per currency, biggest first. */
export function mergeExcluded(
  rows: ReadonlyArray<{ excluded?: ExcludedIncome[] }>
): ExcludedIncome[] {
  const byCurrency = new Map<string, ExcludedIncome>();
  for (const row of rows) {
    for (const e of row.excluded ?? []) {
      const sum = byCurrency.get(e.currency) ?? { currency: e.currency, bookings: 0, income: 0 };
      sum.bookings += e.bookings;
      sum.income += e.income;
      byCurrency.set(e.currency, sum);
    }
  }
  return [...byCurrency.values()].sort((a, b) => b.income - a.income);
}

/**
 * "Not included: 2 bookings in USD ($2,000) and 1 in GBP (£300)": what an
 * income total in the reporting currency leaves out. Null when nothing is.
 */
export function excludedNote(excluded: readonly ExcludedIncome[] | undefined): string | null {
  if (!excluded || excluded.length === 0) return null;
  const parts = excluded.map((e, i) => {
    const what =
      i === 0 ? `${e.bookings} ${e.bookings === 1 ? 'booking' : 'bookings'}` : e.bookings;
    return `${what} in ${e.currency} (${formatMoney(e.income, e.currency)})`;
  });
  const list =
    parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
  return `Not included: ${list}`;
}

function formatter(currency: string | null | undefined): Intl.NumberFormat {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency ?? 'EUR',
    maximumFractionDigits: 0,
  });
}
